import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { analyzeTariffDocument, validateTariffData } from "../../../../../../../../../../services/tariffeAnalysisService";
import TariffPreviewTable from "../TariffPreviewTable";
import TariffeBulkPreviewTable from "../TariffeBulkPreviewTable";

const UploadZone = styled(Paper)(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: "center",
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragging
    ? theme.palette.action.hover
    : theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const TabCaricaEstrai = ({ onAddTariff, uploadState, setUploadState }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [textInput, setTextInput] = useState("");
  
  // Estrai lo stato dal padre
  const { uploadedFile, isAnalyzing, analysisError, extractedData, validationResult } = uploadState;
  
  // Helper per aggiornare lo stato nel padre
  const updateUploadState = (updates) => {
    setUploadState(prev => ({ ...prev, ...updates }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && validateFileType(file)) {
      updateUploadState({ uploadedFile: file });
      analyzeFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && validateFileType(file)) {
      updateUploadState({ uploadedFile: file });
      analyzeFile(file);
    }
  };

  const validateFileType = (file) => {
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    
    if (!validTypes.includes(file.type) && 
        !file.name.endsWith(".csv") && 
        !file.name.endsWith(".xlsx") && 
        !file.name.endsWith(".xls")) {
      alert("Formato file non valido. Carica PDF, Immagini, Excel o CSV.");
      return false;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert("File troppo grande. Massimo 10MB.");
      return false;
    }

    return true;
  };

  const analyzeFile = async (file) => {
    updateUploadState({ 
      isAnalyzing: true, 
      analysisError: null, 
      extractedData: null, 
      validationResult: null 
    });

    try {
      const result = await analyzeTariffDocument(file);

      if (result.success && result.data) {
        // result.data is now an array of tariffs
        const dataArray = Array.isArray(result.data) ? result.data : [result.data];
        
        // Validate all tariffs
        const validations = dataArray.map(tariff => validateTariffData(tariff));
        
        updateUploadState({ 
          extractedData: dataArray, 
          validationResult: validations,
          isAnalyzing: false 
        });
      } else {
        updateUploadState({ 
          analysisError: result.error || "Impossibile analizzare il documento",
          isAnalyzing: false 
        });
      }
    } catch (error) {
      console.error("Errore analisi:", error);
      updateUploadState({ 
        analysisError: "Errore durante l'analisi del documento",
        isAnalyzing: false 
      });
    }
  };

  const handleTextAnalyze = async () => {
    if (!textInput.trim()) {
      alert("Inserisci del testo da analizzare");
      return;
    }

    // Create a virtual file from text
    const blob = new Blob([textInput], { type: "text/plain" });
    const file = new File([blob], "text-input.txt", { type: "text/plain" });
    
    updateUploadState({ uploadedFile: file });
    await analyzeFile(file);
  };

  const handleRemoveFile = () => {
    updateUploadState({
      uploadedFile: null,
      extractedData: null,
      validationResult: null,
      analysisError: null,
    });
    setTextInput("");
  };

  const handleSaveTariff = (tariffe = null) => {
    const dataToSave = tariffe || extractedData;
    if (dataToSave && onAddTariff) {
      // Pass data as-is (can be array or single object)
      onAddTariff(dataToSave);
      handleRemoveFile();
      
      if (Array.isArray(dataToSave)) {
        alert(`${dataToSave.length} tariffa/e salvata/e con successo!`);
      } else {
        alert("Tariffa salvata con successo!");
      }
    }
  };

  const handleUpdateExtractedData = (updatedData) => {
    const validation = Array.isArray(updatedData) 
      ? updatedData.map(tariff => validateTariffData(tariff))
      : validateTariffData(updatedData);
    
    updateUploadState({ 
      extractedData: updatedData,
      validationResult: validation 
    });
  };

  return (
    <Box>
      {/* Upload Zone */}
      {!uploadedFile && (
        <>
          <UploadZone
            isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-input").click()}
          >
            <input
              id="file-input"
              type="file"
              hidden
              accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx,.xls"
              onChange={handleFileSelect}
            />
            <CloudUploadIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Trascina qui il documento o clicca per selezionare
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Formati supportati: PDF, Immagini, Excel, CSV
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Dimensione massima: 10MB
            </Typography>
          </UploadZone>

          {/* Text Input Option */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Oppure incolla i dati testuali
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Incolla qui i dati della tariffa (es. da una scheda di confrontabilità, CTE, ecc.)"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              variant="outlined"
            />
            <Button
              variant="contained"
              onClick={handleTextAnalyze}
              disabled={!textInput.trim() || isAnalyzing}
              sx={{ mt: 2 }}
            >
              Analizza Testo
            </Button>
          </Box>
        </>
      )}

      {/* File Info */}
      {uploadedFile && !isAnalyzing && (
        <Paper sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <CheckCircleIcon color="success" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={600}>
              {uploadedFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(uploadedFile.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>
          <IconButton onClick={handleRemoveFile} color="error">
            <DeleteIcon />
          </IconButton>
        </Paper>
      )}

      {/* Loading */}
      {isAnalyzing && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Analisi in corso con AI...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Estrazione dati dalla tariffa
          </Typography>
        </Box>
      )}

      {/* Error */}
      {analysisError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {analysisError}
        </Alert>
      )}

      {/* Preview Table */}
      {extractedData && validationResult && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">
              {Array.isArray(extractedData) ? `${extractedData.length} Tariffe Estratte` : "Tariffa Estratta"}
            </Typography>
          </Box>
          
          {/* Validation Alerts */}
          {Array.isArray(validationResult) ? (
            validationResult.some(v => v && v.errors && v.errors.length > 0) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>Errori rilevati in alcune tariffe</Typography>
              </Alert>
            )
          ) : validationResult && validationResult.errors && validationResult.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Errori rilevati:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationResult.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {!Array.isArray(validationResult) && validationResult && validationResult.warnings && validationResult.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Attenzione:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationResult.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}

          {Array.isArray(extractedData) ? (
            <TariffeBulkPreviewTable
              tariffe={extractedData}
              onUpdate={handleUpdateExtractedData}
              onSaveAll={handleSaveTariff}
            />
          ) : (
            <>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="h6">
                  Anteprima Dati Estratti (Editabile)
                </Typography>
                <Chip
                  label={`Confidence: ${extractedData.confidenceScore}%`}
                  color={extractedData.confidenceScore >= 80 ? "success" : "warning"}
                  size="small"
                />
              </Box>
              <TariffPreviewTable
                data={extractedData}
                onUpdate={handleUpdateExtractedData}
              />

              <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={handleRemoveFile}>
                  Annulla
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveTariff}
                  disabled={validationResult && !validationResult.isValid}
                >
                  Salva Tariffa
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default TabCaricaEstrai;
